import { Injectable } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import {
  BdcCreateLegalEntityPayload,
  LegalEntityDetails,
  BdcCheckPropertyStatus,
  BdcContactInfo,
  BdcCreateOrUpdatePropertyDetailsResponseData,
  BdcCreatePropertyDetailsPayload,
  BdcPropertyExtendedFacility,
  BdcPropertyFacility,
  BdcPropertySettingsAndPolicies,
  BdcRoomExtendedFacility,
  BdcRoomFacility,
  BdcUpdatePropertyStatusPayload,
  BdcUnitResponse,
  BdcCreateUnitPayload,
  BdcUpdateUnitPayload,
  BdcPropertyDetailsResponse,
  BdcUpdatePropertyDetailsPayload,
  BdcCreateOrUpdateRatePlanPayload,
  BdcLicenseRequirementsWithValues,
  BdcSendLicenseInfoPayloadData,
  BdcSendLicenseResponse,
  BdcCreateProductPayload,
  BdcUpdateProductPayload,
  BdcProductResponse,
  BdcRatePlanResponse,
  BdcUnitMetadataResponse,
  BdcPhotoMetadata,
  BdcPropertyPhotoGalleryData,
  BdcRoomPhotoGalleryData,
  BdcUpdateRoomPhotoGalleryPayload,
  BdcUpdatePropertyPhotoGalleryPayload,
  BdcPendingPhoto,
  BdcPhotoTag,
  BdcPropertyExtendedFacilityMetadataItem,
  BdcRoomExtendedFacilityMetadataItem,
  BdcPolicy,
  BdcCheckPropertyStatusOperation,
  BdcFacilityMeta,
  BdcBathroomDetails,
} from './model-v2'
import { map } from 'rxjs/operators'
import { HttpGetRequestCacheService, isSomething } from '@tokeet-frontend/tv3-platform'

@Injectable()
export class BdcContentV2Service {
  constructor(private http: HttpClient, private httpCache: HttpGetRequestCacheService) {}

  createLegalEntity(data: BdcCreateLegalEntityPayload) {
    return this.http.post<LegalEntityDetails>('@api/bdcv2/contracts', data)
  }

  getLegalEntity(entityId: string) {
    return this.http.get<LegalEntityDetails>(`@api/bdcv2/contracts/${entityId}`)
  }

  // TV4-6504: the channel manager refuses property creation for an account that support has not approved
  getCreationApproval() {
    return this.http.get<{ approved: boolean }>('@api/bdcv2/creation-approval')
  }

  // Properties
  createProperty(data: BdcCreatePropertyDetailsPayload) {
    return this.http.post<BdcCreateOrUpdatePropertyDetailsResponseData>('@api/bdcv2/properties', data)
  }

  getProperty(propertyId: number) {
    return this.http.get<BdcPropertyDetailsResponse>(`@api/bdcv2/properties/${propertyId}`).pipe(
      map((property) => {
        property.stars = property.stars ? +property.stars : 0
        if (property.physical_address?.country_code) {
          property.physical_address.country_code = String(property.physical_address.country_code).toUpperCase()
        }
        return property
      })
    )
  }

  updateProperty(propertyId: number, data: BdcUpdatePropertyDetailsPayload) {
    return this.http.put<BdcPropertyDetailsResponse>(`@api/bdcv2/properties/${propertyId}`, data)
  }

  getPropertySettings(propertyId: number) {
    return this.http.get<BdcPropertySettingsAndPolicies>(`@api/bdcv2/properties/${propertyId}/settings`)
  }

  updatePropertySettings(propertyId: number, data: BdcPropertySettingsAndPolicies) {
    return this.http.put<BdcPropertySettingsAndPolicies>(`@api/bdcv2/properties/${propertyId}/settings`, data)
  }

  updatePropertyContacts(propertyId: number, data: BdcContactInfo) {
    return this.http.put<BdcContactInfo>(`@api/bdcv2/properties/${propertyId}/contacts`, data)
  }

  getPropertyContacts(propertyId: number) {
    return this.http.get<BdcContactInfo>(`@api/bdcv2/properties/${propertyId}/contacts`)
  }

  changePropertyStatus(propertyId: number, data: BdcUpdatePropertyStatusPayload) {
    return this.http.post<BdcCheckPropertyStatus>(`@api/bdcv2/properties/${propertyId}/status`, data)
  }

  getPropertyUnits(propertyId: number) {
    return this.http.get<BdcUnitResponse[]>(`@api/bdcv2/properties/${propertyId}/units`)
  }

  createPropertyUnit(propertyId: number, data: BdcCreateUnitPayload) {
    return this.http.post<BdcUnitResponse>(`@api/bdcv2/properties/${propertyId}/units`, data)
  }

  updatePropertyUnit(propertyId: number, unitId: number, data: BdcUpdateUnitPayload) {
    return this.http.put<BdcUnitResponse>(`@api/bdcv2/properties/${propertyId}/units/${unitId}`, data)
  }

  deletePropertyUnit(propertyId: number, unitId: number) {
    return this.http.delete(`@api/bdcv2/properties/${propertyId}/units/${unitId}`)
  }

  getPropertyRatePlans(propertyId: number) {
    return this.http.get<BdcRatePlanResponse[]>(`@api/bdcv2/properties/${propertyId}/rate-plans`)
  }

  createPropertyRatePlans(propertyId: number, data: BdcCreateOrUpdateRatePlanPayload) {
    return this.http.post<BdcRatePlanResponse>(`@api/bdcv2/properties/${propertyId}/rate-plans`, data)
  }

  updatePropertyRatePlan(propertyId: number, ratePlanId: number, data: BdcCreateOrUpdateRatePlanPayload) {
    return this.http.put(`@api/bdcv2/properties/${propertyId}/rate-plans/${ratePlanId}`, data)
  }

  getPropertyProducts(propertyId: number) {
    return this.http.get<BdcProductResponse[]>(`@api/bdcv2/properties/${propertyId}/products`)
  }

  createPropertyProducts(propertyId: number, data: BdcCreateProductPayload) {
    return this.http.post<BdcCreateProductPayload>(`@api/bdcv2/properties/${propertyId}/products`, data)
  }

  updatePropertyProduct(propertyId: number, roomId: number, ratePlanId: number, data: BdcUpdateProductPayload) {
    return this.http.put<BdcCreateProductPayload>(
      `@api/bdcv2/properties/${propertyId}/products/rooms/${roomId}/rate-plans/${ratePlanId}`,
      data
    )
  }

  deletePropertyProduct(propertyId: number, roomId: number, ratePlanId: number) {
    return this.http.delete(`@api/bdcv2/properties/${propertyId}/products/rooms/${roomId}/rate-plans/${ratePlanId}`)
  }

  getPropertyPhotos(propertyId: number) {
    return this.http.get<BdcPhotoMetadata[]>(`@api/bdcv2/properties/${propertyId}/photos`)
  }

  getPropertyPendingPhotos(propertyId: number) {
    return this.http.get<BdcPendingPhoto[]>(`@api/bdcv2/properties/${propertyId}/photos/pending`)
  }

  uploadPropertyPhotos(propertyId: number, data: { url: string; tag_ids: number[] }[]) {
    return this.http.post<BdcPendingPhoto[]>(`@api/bdcv2/properties/${propertyId}/photos/pending`, { photos: data })
  }

  addPhotoTags(propertyId: number, photoId: string, tagIds: number[]) {
    return this.http.post(`@api/bdcv2/properties/${propertyId}/photos/${photoId}/tags`, {
      tag_ids: tagIds.map((id) => +id),
    })
  }

  getPropertyPhotoGallery(propertyId: number) {
    return this.http.get<BdcPropertyPhotoGalleryData>(`@api/bdcv2/properties/${propertyId}/photos/gallery`)
  }

  updatePropertyPhotoGallery(propertyId: number, data: BdcUpdatePropertyPhotoGalleryPayload) {
    return this.http.post(`@api/bdcv2/properties/${propertyId}/photos/gallery`, data)
  }

  getRoomPhotoGallery(propertyId: number, roomId: number) {
    return this.http.get<BdcRoomPhotoGalleryData>(`@api/bdcv2/properties/${propertyId}/rooms/${roomId}/photos/gallery`)
  }

  updateRoomPhotoGallery(propertyId: number, roomId: number, data: BdcUpdateRoomPhotoGalleryPayload) {
    return this.http.post(`@api/bdcv2/properties/${propertyId}/rooms/${roomId}/photos/gallery`, data)
  }

  deletePropertyPhoto(propertyId: number, photoId: string) {
    return this.http.delete(`@api/bdcv2/properties/${propertyId}/photos/${photoId}`)
  }

  // Metadata
  private fixSpaces: Record<string, string> = {
    WAKE_UP_SERVICEALARMCLOCK: 'Wake Up Service Alarm Clock',
    WARDROBECLOSET: 'Wardrobe Closet',
    SEMIDETACHED: 'Semi-Detached',
    BABYSITTINGCHILD_SERVICES: 'Babysitting Child Services',
    SOUNDPROOFROOMS: 'Soundproof Rooms',
    MEETINGBANQUET_FACILITIES: 'Meeting Banquet Facilities',
    HORSERIDING: 'Horse Riding',
  }
  getPropertyAmenities() {
    return this.httpCache
      .cacheGetRequest<BdcPropertyExtendedFacilityMetadataItem[]>('@api/bdcv2/metadata/property-amenities')
      .pipe(
        map((items) =>
          items.map((t) => ({
            ...t,
            facility_name: this.fixSpaces[t.facility_name] || t.facility_name,
          }))
        )
      )
  }

  getRoomAmenities() {
    return this.httpCache
      .cacheGetRequest<BdcRoomExtendedFacilityMetadataItem[]>('@api/bdcv2/metadata/room-amenities')
      .pipe(
        map((items) =>
          items.map((t) => ({
            ...t,
            room_facility_name: this.fixSpaces[t.room_facility_name] || t.room_facility_name,
          }))
        )
      )
  }

  getAllPhotoTags() {
    return this.httpCache.cacheGetRequest<BdcPhotoTag[]>(`@api/bdcv2/metadata/photo-tags`)
  }

  /** @deprecated */
  getRoomTypes() {
    return this.httpCache.cacheGetRequest<Record<string, number>>('@api/bdcv2/metadata/room-types')
  }

  /** @deprecated */
  getBedTypes() {
    return this.httpCache.cacheGetRequest<{ label: string; index: number }[]>('@api/bdcv2/metadata/bed-types')
  }

  getUnitMetadata() {
    return this.httpCache.cacheGetRequest<BdcUnitMetadataResponse>('@api/bdcv2/metadata/units')
  }

  // Facilities
  getPropertyFacilities(propertyId: number) {
    return this.http.get<BdcPropertyExtendedFacility[]>(`@api/bdcv2/properties/${propertyId}/facilities`)
  }

  updatePropertyFacilities(propertyId: number, data: BdcPropertyFacility[]) {
    return this.http.put<BdcPropertyExtendedFacility[]>(`@api/bdcv2/properties/${propertyId}/facilities`, data)
  }

  getRoomFacilities(propertyId: number, roomId: number) {
    return this.http.get<BdcRoomExtendedFacility[]>(`@api/bdcv2/properties/${propertyId}/rooms/${roomId}/facilities`)
  }

  updateRoomFacilities(propertyId: number, roomId: number, data: BdcRoomFacility[]) {
    return this.http.put<BdcRoomExtendedFacility[]>(
      `@api/bdcv2/properties/${propertyId}/rooms/${roomId}/facilities`,
      data
    )
  }

  // Bathrooms
  getFacilitiesMeta() {
    return this.httpCache.cacheGetRequest<BdcFacilityMeta>('@api/bdcv2/metadata/facilities')
  }

  getRoomBathrooms(propertyId: number, roomId: number) {
    return this.http.get<BdcBathroomDetails>(`@api/bdcv2/properties/${propertyId}/rooms/${roomId}/bathrooms`)
  }

  updateRoomBathrooms(propertyId: number, roomId: number, data: BdcBathroomDetails) {
    return this.http.put<BdcBathroomDetails>(`@api/bdcv2/properties/${propertyId}/rooms/${roomId}/bathrooms`, data)
  }

  // Licenses
  getPropertyLicenses(propertyId: number) {
    return this.http.get<BdcLicenseRequirementsWithValues>(`@api/bdcv2/properties/${propertyId}/licenses`)
  }

  updatePropertyLicenses(propertyId: number, data: BdcSendLicenseInfoPayloadData) {
    return this.http.put<BdcSendLicenseResponse>(`@api/bdcv2/properties/${propertyId}/licenses`, data)
  }

  getRoomLicenses(propertyId: number, roomId: number) {
    return this.http.get<BdcLicenseRequirementsWithValues>(
      `@api/bdcv2/properties/${propertyId}/rooms/${roomId}/licenses`
    )
  }

  updateRoomLicenses(propertyId: number, roomId: number, data: BdcSendLicenseInfoPayloadData) {
    return this.http.put<BdcSendLicenseResponse>(`@api/bdcv2/properties/${propertyId}/rooms/${roomId}/licenses`, data)
  }

  // Policies
  getPropertyPolicies(propertyId: number) {
    return this.http.get<BdcPolicy[]>(`@api/bdcv2/properties/${propertyId}/policies`)
  }

  // Property Attributes
  /** @deprecated */
  getPropertyAttrs(propertyId: number) {
    return this.http.get(`@api/bdcv2/property/${propertyId}/attrs`)
  }

  checkPropertyStatus(propertyId: number, payload: BdcUpdatePropertyStatusPayload) {
    return this.http.post<BdcCheckPropertyStatus>(`@api/bdcv2/properties/${propertyId}/status`, payload).pipe(
      map((res) => {
        if (isSomething(res.errors) && payload.operation !== BdcCheckPropertyStatusOperation.CHECK) {
          throw new Error(res.errors.join(', '))
        }
        return res
      })
    )
  }
}
